import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
  getWalletAccountAddressByType,
} from '@dynamic-labs-sdk/client';
import type { WalletProviderConnectAddress } from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getBuffer,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type { BitcoinWalletProvider } from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import type { PhantomBitcoinInjectedProvider } from '../PhantomBitcoinInjectedProvider.types';

export const PHANTOM_METADATA: WalletProviderMetadata = {
  displayName: 'Phantom',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#phantom`,
  supportedHardwareWalletVendors: ['ledger'],
};

type CreatePhantomBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: PhantomBitcoinInjectedProvider;
};

export const createPhantomBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreatePhantomBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: PHANTOM_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const accounts = await injectedProvider.requestAccounts();

    const addresses: WalletProviderConnectAddress[] = accounts.map(
      (account) => ({
        address: account.address,
        publicKey: account.publicKey,
        type: account.purpose === 'payment' ? 'payment' : 'ordinals',
      })
    );

    // put ordinals addresses first, as they should be the main walletAccount address
    addresses.sort((a) => (a.type === 'ordinals' ? -1 : 1));

    return {
      addresses,
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  // there's no specific method to get connected addresses in Phantom
  // so we use the connect method to get the addresses
  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const { addresses } = await connect();
      return { addresses: addresses.map((address) => address.address) };
    };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    addressType,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    let addressToSignWith = walletAccount.address;
    if (addressType) {
      addressToSignWith =
        getWalletAccountAddressByType({
          type: addressType,
          walletAccount,
        }) ?? addressToSignWith;
    }

    const result = await injectedProvider.signMessage(
      addressToSignWith,
      new TextEncoder().encode(message)
    );

    return {
      signature: getBuffer().from(result.signature).toString('base64'),
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

    const inputsToSign =
      request.signature?.map((sig) => ({
        address: sig.address,
        sigHash: request.allowedSighash[0],
        signingIndexes: sig.signingIndexes ?? [],
      })) ?? [];

    const signedPsbt = await injectedProvider.signPSBT(
      psbtFromBase64.toBuffer(),
      {
        inputsToSign,
      }
    );

    return { signedPsbt: Psbt.fromBuffer(signedPsbt).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedPsbts: string[] = [];

    for (const request of requests) {
      const signedPsbtResponse = await signPsbt({ request, walletAccount });

      if (signedPsbtResponse) {
        signedPsbts.push(signedPsbtResponse.signedPsbt);
      }
    }

    return {
      signedPsbts,
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        injectedProvider?.removeAllListeners();
      },

      setupEventListeners: ({ handleAccountsChanged }) => {
        injectedProvider?.on('accountsChanged', (accounts) => {
          handleAccountsChanged({
            addresses: accounts.map((account) => account.address),
          });
        });
      },

      supportedEvents: ['accountsChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(PHANTOM_METADATA.displayName),
    key,
    metadata: PHANTOM_METADATA,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
